import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { iaRa } from 'lib/helpers/utility';
import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';

import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import { clinicalNote as clinicalNoteState } from 'components/state';
import { getMedicationDeleteContextMessage } from 'components/Forms/Custom/ProviderMedications/lib/helpers';
import {
  CONFIRMATION_MODAL,
  MEDICATION_ACTION_TYPES,
  MEDICATION_STATUS,
  VERADIGM_MEDICATION_READ_ONLY_TOOLTIP
} from 'components/Forms/Custom/ProviderMedications/lib/constants';

import { useMedicationActionMutations } from './lib/useMedicationActionMutations';

const Actions = ({ data, customProps }) => {
  const [confirmationModal, setConfirmationModal] = useState(CONFIRMATION_MODAL);

  const clinicalNote = useRecoilValue(clinicalNoteState);

  const { id: patientId, appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { saveNoteRef } = useClinicalNoteContext() || {};

  const resolvedPatientId = customProps?.patientId || patientId;
  const handleStatusLabel = data?.status === MEDICATION_STATUS.ACTIVE ? 'inactive' : 'active';
  const disabledReason = data?.veradigm_id
    ? VERADIGM_MEDICATION_READ_ONLY_TOOLTIP
    : customProps?.disableDefaultActions?.(data);
  const defaultActionsDisabled = Boolean(disabledReason);
  const disabledTooltip = typeof disabledReason === 'string' ? disabledReason : null;

  const options = [
    ...iaRa(customProps?.getExtraOptions?.(data)),
    {
      icon: 'status-mono',
      color: data?.status === MEDICATION_STATUS.ACTIVE ? 'danger' : 'primary',
      stroke: true,
      label: `Mark ${handleStatusLabel}`,
      disabled: defaultActionsDisabled,
      tooltip: disabledTooltip,
      onClick: () =>
        setConfirmationModal({ open: true, type: MEDICATION_ACTION_TYPES.UPDATE_STATUS })
    },
    {
      icon: 'edit-2-linear',
      label: 'Edit',
      disabled: defaultActionsDisabled,
      tooltip: disabledTooltip,
      onClick: () => onHandleEdit()
    },
    {
      icon: 'trash-mono',
      label: 'Delete',
      disabled: defaultActionsDisabled,
      tooltip: disabledTooltip,
      onClick: () => setConfirmationModal({ open: true, type: MEDICATION_ACTION_TYPES.DELETE })
    }
  ];

  const fromClinicalNote = () => {
    if (location.pathname.includes('clinical-note')) {
      return true;
    }
    return false;
  };

  const onHandleDelete = async () => {
    await mutateDeleteMedication.mutateAsync();

    if (customProps?.gridApi) {
      customProps.gridApi.deselectAll();
    }
    setConfirmationModal(CONFIRMATION_MODAL);
  };

  const onHandleEdit = () => {
    customProps?.setShowMedicationModal({ open: true, data });
    if (customProps?.gridApi) {
      customProps.gridApi.deselectAll();
    }
  };

  const onHandleUpdateStatus = async () => {
    let clinicalNoteId = clinicalNote?.id;
    if (fromClinicalNote() && !clinicalNote?.id) {
      const savedNote = await saveNoteRef?.current?.checkPaymentBeforeSaving?.();
      clinicalNoteId = savedNote?.id;
      if (!clinicalNoteId) return;
    }

    await upsertFormFunctionMutation.mutateAsync({
      patientId: resolvedPatientId,
      clinicalNoteId,
      appointmentId,
      practice_id: data?.practice_id,
      medication: {
        medications: [
          {
            ...data,
            status:
              data?.status === MEDICATION_STATUS.ACTIVE
                ? MEDICATION_STATUS.INACTIVE
                : MEDICATION_STATUS.ACTIVE
          }
        ]
      }
    });

    if (data?.weight_loss_medication_id) {
      const newStatus =
        data?.status === MEDICATION_STATUS.ACTIVE
          ? MEDICATION_STATUS.INACTIVE
          : MEDICATION_STATUS.ACTIVE;

      await upsertWeightLossMutation.mutateAsync({
        patientId: resolvedPatientId,
        appointmentId,
        clinicalNoteId,
        weightLossAdministration: {
          id: data.weight_loss_medication_id,
          status: newStatus
        }
      });
    }
    if (customProps?.gridApi) {
      customProps.gridApi.deselectAll();
    }
    setConfirmationModal(CONFIRMATION_MODAL);
  };

  const { mutateDeleteMedication, upsertFormFunctionMutation, upsertWeightLossMutation } =
    useMedicationActionMutations({ data, patientId: resolvedPatientId, navigate });

  const isSaving = upsertFormFunctionMutation?.isPending || upsertWeightLossMutation?.isPending;

  return (
    <div className="flex h-full w-full items-center justify-center">
      <Popover
        options={options}
        optionIconColor="primary"
        position="left"
        isFixed
        icon="context-menu-vertical"
      />

      <Confirm
        icon="info-filled"
        iconColor="danger"
        iconSize={42}
        variant="danger"
        title="Delete medication"
        message={`Are you sure you want to delete this medication? ${getMedicationDeleteContextMessage(data)}`}
        isOpen={
          !!confirmationModal?.open && confirmationModal?.type === MEDICATION_ACTION_TYPES.DELETE
        }
        handleClose={() => setConfirmationModal(CONFIRMATION_MODAL)}
        footerActions={{
          primary: {
            text: 'Delete',
            onClick: onHandleDelete,
            color: 'danger',
            isLoading: mutateDeleteMedication?.isLoading,
            isDisabled: mutateDeleteMedication?.isLoading
          }
        }}
      />

      <Confirm
        icon="info-filled"
        iconSize={42}
        variant="warning"
        title={`Mark ${handleStatusLabel} medication`}
        message={`Are you sure you want to mark this medication as ${handleStatusLabel}?`}
        isOpen={
          !!confirmationModal?.open &&
          confirmationModal?.type === MEDICATION_ACTION_TYPES.UPDATE_STATUS
        }
        handleClose={() => setConfirmationModal(CONFIRMATION_MODAL)}
        footerActions={{
          primary: {
            text: `Mark ${handleStatusLabel}`,
            onClick: onHandleUpdateStatus,
            color: 'warning',
            isLoading: isSaving,
            isDisabled: isSaving
          }
        }}
      />
    </div>
  );
};

export default Actions;
